import React, { useRef, useEffect, useState, memo } from "react";
import { GridProps, Grid } from "../grid/grid";
import { CalendarProps, Calendar } from "../calendar/calendar";
import { TaskGanttContentProps, TaskGanttContent } from "./task-gantt-content";
import styles from "./gantt.module.css";
import { Task, ViewMode } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";

export type TaskGanttProps = {
  gridProps: GridProps;
  calendarProps: CalendarProps;
  barProps: TaskGanttContentProps;
  ganttHeight: number;
  scrollY: number;
  scrollX: number;
  viewMode: ViewMode;
  depandanyData: (data: any) => void;
  onDepandancyDragEnd: (data: any, data2: any) => void;
  onDepandancyDraging: (data: any, data2: any) => void;
  selectedTasks: Task | undefined;
  onselect: (data: { x: number; y: number }) => void;
  svgWidth: number;
  canCreateDependency: boolean;
};

const TaskGanttComponent: React.FC<TaskGanttProps> = ({
  gridProps,
  calendarProps,
  barProps,
  ganttHeight,
  scrollY,
  scrollX,
  viewMode,
  onDepandancyDragEnd,
  onDepandancyDraging,
  depandanyData,
  selectedTasks,
  onselect,
  canCreateDependency
}) => {
  const ganttSVGRef = useRef<SVGSVGElement>(null);
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const [e, sete] = useState<any>();
  const [endDrag, setEndDrag] = useState<any>();
  const verticalGanttContainerRef = useRef<HTMLDivElement>(null);

  const onselectionchange = () => {
    onselect({
      x: verticalGanttContainerRef.current?.scrollLeft ?? 0,
      y: horizontalContainerRef.current?.scrollTop ?? 0
    });
  };

  useEffect(() => {
    if (!selectedTasks) return;

    const horizontalContainer = horizontalContainerRef.current;
    const verticalContainer = verticalGanttContainerRef.current;

    if (!horizontalContainer || !verticalContainer) return;

    const task = selectedTasks as BarTask;
    const horizontalPadding = 20;
    const scrollToX = task.x1 - horizontalPadding;
    verticalContainer.scrollLeft = Math.max(scrollToX, 0);

    const taskY = task.y;
    const taskHeight = task.height ?? barProps.taskHeight;
    const containerHeight = horizontalContainer.clientHeight;

    const scrollToY = taskY - containerHeight / 2 + taskHeight / 2;
    horizontalContainer.scrollTop = Math.max(scrollToY, 0);

    onselect({
      x: verticalContainer.scrollLeft,
      y: horizontalContainer.scrollTop ?? 0
    });
  }, [selectedTasks]);

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  useEffect(() => {
    if (verticalGanttContainerRef.current) {
      verticalGanttContainerRef.current.scrollLeft = scrollX;
    }
  }, [scrollX]);

  const newBarProps = {
    ...barProps,
    svg: ganttSVGRef,
    e: e,
    endDrag: endDrag,
    onDepandancyDragEnd: onDepandancyDragEnd,
    onDepandancyDraging: onDepandancyDraging,
    depandanyData: depandanyData,
    selectedTasks: selectedTasks,
    onselectionchange: onselectionchange,
    ganttSVGRef,
    canCreateDependency
  };

  return (
    <div className={styles.ganttVerticalContainer} ref={verticalGanttContainerRef} dir="ltr">
      <div style={{ position: "sticky", top: 0, zIndex: 0 }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={gridProps.svgWidth}
          height={calendarProps.headerHeight}
          fontFamily={barProps.fontFamily}
        >
          <Calendar {...calendarProps} />
        </svg>
      </div>
      <div
        ref={horizontalContainerRef}
        className={styles.horizontalContainer}
        style={
          ganttHeight
            ? { height: ganttHeight, width: gridProps.svgWidth }
            : { width: gridProps.svgWidth }
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={gridProps.svgWidth}
          height={barProps.rowHeight * barProps.tasks.length}
          fontFamily={barProps.fontFamily}
          ref={ganttSVGRef}
          className="dragGrid"
        >
          <Grid
            {...gridProps}
            viewMode={viewMode}
            sete={sete}
            setEndDrag={setEndDrag}
            selectedTasks={selectedTasks}
          />
          <TaskGanttContent {...newBarProps} />
        </svg>
      </div>
    </div>
  );
};

export const TaskGantt = memo(TaskGanttComponent);
